import React from "react";
import { sendWhatsAppMessage } from "../../../extras";
import { validatePhoneWithDDD } from "../../../common/validators";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { alpha, styled, useTheme } from "@mui/material/styles";

// ICONS
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { phoneMask } from "../../../utils/formatters";

// Styled components
const ProfileHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.dark,
  width: 52,
  height: 52,
  fontSize: "1.25rem",
  fontWeight: 700,
}));

const ContactRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.5),
  borderRadius: 4,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1),
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: theme.palette.background.secondary,
  },
}));

const ContactIconBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: theme.spacing(1.5),
  "& .MuiSvgIcon-root": {
    fontSize: "1.25rem",
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  width: 32,
  height: 32,
  borderRadius: 4,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  border: `1px dashed ${theme.palette.grey[400]}`,
}));

export default function CustomerInfos({ customerName, customer, contact }) {
  const theme = useTheme();
  const { name, phonenmbr, email } = customer || {};
  const phoneNumber = contact || phonenmbr;
  const displayName = customerName || (customer && name) || null;
  const hasCustomerInfo = displayName || phoneNumber || email;

  return (
    <Box className="cashier-info-customer-step">
      <ProfileHeader>
        <ProfileAvatar>
          {displayName ? (
            displayName.charAt(0).toUpperCase()
          ) : (
            <PersonOutlineIcon fontSize="large" />
          )}
        </ProfileAvatar>

        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
            }}
          >
            {displayName || "Cliente não identificado"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasCustomerInfo
              ? "Informações de contato"
              : "Sem informações detalhadas"}
          </Typography>
        </Box>
      </ProfileHeader>

      {!hasCustomerInfo ? (
        <EmptyState>
          <AccountCircleIcon
            sx={{
              fontSize: 40,
              color: theme.palette.grey[400],
              mb: 1,
            }}
          />
          <Typography variant="body2" color="text.secondary" align="center">
            Nenhuma informação de contato disponível para este cliente.
          </Typography>
        </EmptyState>
      ) : (
        <Stack spacing={1}>
          {phoneNumber && (
            <ContactRow>
              <ContactIconBox sx={{ color: theme.palette.primary.main }}>
                <ContactPhoneIcon />
              </ContactIconBox>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: theme.palette.text.secondary }}
              >
                Telefone
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ml: "auto",
                  mr: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {phonenmbr ? phoneMask(phonenmbr) : contact}
              </Typography>
              {validatePhoneWithDDD(phoneNumber) && (
                <Tooltip title="Enviar mensagem via WhatsApp">
                  <ActionButton
                    size="small"
                    onClick={() => sendWhatsAppMessage(phoneNumber)}
                    aria-label="contact customer whatsapp"
                    sx={{
                      color: theme.palette.success.main,
                      backgroundColor: alpha(theme.palette.success.main, 0.08),
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          0.16
                        ),
                      },
                    }}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </ActionButton>
                </Tooltip>
              )}
            </ContactRow>
          )}

          {email && (
            <ContactRow>
              <ContactIconBox sx={{ color: theme.palette.error.main }}>
                <AlternateEmailIcon />
              </ContactIconBox>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: theme.palette.text.secondary }}
              >
                Email
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ml: "auto",
                  mr: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {email}
              </Typography>
              <Tooltip title="Copiar email">
                <ActionButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(email)}
                  aria-label="copy email"
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.16),
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            </ContactRow>
          )}
        </Stack>
      )}
    </Box>
  );
}
